import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import type { ApiError } from '../../../lib/errors';
import { triThucRepository } from '../../../lib/repositories/tri-thuc.repository';
import { useToast } from '../../../lib/toast';
import { kiemTep } from '../nghiep-vu/tri-thuc';
import type { LoiTep } from '../nghiep-vu/tri-thuc';
import type { TrangThaiMuc } from '../types';
import { KHOA_TRI_THUC } from './useTriThuc';

export interface MucHangDoi {
  khoa: string;
  tep: File;
  kbId: string | undefined;
  tieuDe: string;
  tomTat: string;
  trangThai: TrangThaiMuc;
  /** Đã thử gửi ít nhất một lần: lỗi thiếu ô mới hiện từ đây. */
  daGui: boolean;
  loi?: ApiError;
}

/** Trần tiêu đề tài liệu (`tai-lieu.service.ts`). */
export const DAI_TIEU_DE = 500;

/** Mục đủ ô để gửi chưa — tiêu đề và bộ bắt buộc. */
export function mucHopLe(m: Pick<MucHangDoi, 'kbId' | 'tieuDe'>): boolean {
  return Boolean(m.kbId) && m.tieuDe.trim().length > 0 && m.tieuDe.trim().length <= DAI_TIEU_DE;
}

/**
 * Hàng đợi của màn "Upload tài liệu" (native `useUploadTaiLieu`): mỗi tệp chờ người dùng chọn bộ và đặt
 * tiêu đề, rồi gửi lần lượt thành tài liệu nháp (`POST /documents`). Hàng đợi chỉ sống trong trang — tải
 * lại trang là mất những mục chưa gửi.
 */
export function useHangDoiTaiLieu() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t, locale } = useT();
  const [ds, setDs] = useState<MucHangDoi[]>([]);
  const [dangChay, setDangChay] = useState(false);

  const sua = (khoa: string, doi: Partial<MucHangDoi>) =>
    setDs((cu) => cu.map((m) => (m.khoa === khoa ? { ...m, ...doi } : m)));

  /** Thêm tệp; tệp backend sẽ từ chối thì trả về kèm lý do, không vào hàng đợi. */
  const them = (tep: File): LoiTep | null => {
    const ma = kiemTep(tep);
    if (ma) return ma;
    setDs((cu) => [
      ...cu,
      {
        khoa: `${tep.name}-${tep.size}-${tep.lastModified}-${cu.length}`,
        tep,
        kbId: undefined,
        // Tên tệp bỏ đuôi làm tiêu đề gợi ý, như native.
        tieuDe: tep.name.replace(/\.[^.]+$/, ''),
        tomTat: '',
        trangThai: 'cho',
        daGui: false,
      },
    ]);
    return null;
  };

  const gui = async (m: MucHangDoi): Promise<boolean> => {
    if (!mucHopLe(m)) {
      sua(m.khoa, { daGui: true });
      return false;
    }
    sua(m.khoa, { daGui: true, trangThai: 'dang', loi: undefined });
    try {
      await triThucRepository.taoTaiLieu({
        knowledge_base_id: m.kbId!,
        title: m.tieuDe.trim(),
        ...(m.tomTat.trim() ? { content_summary: m.tomTat.trim() } : {}),
        tep: m.tep,
      });
      sua(m.khoa, { trangThai: 'xong' });
      return true;
    } catch (err) {
      sua(m.khoa, { trangThai: 'loi', loi: err as ApiError });
      return false;
    }
  };

  const ketThuc = (soXong: number) => {
    if (soXong === 0) return;
    void queryClient.invalidateQueries({ queryKey: KHOA_TRI_THUC.taiLieu });
    toast.success(t('triThuc.upload.daTaiLen', { so: soVN(soXong, locale) }));
  };

  const guiMot = async (khoa: string) => {
    const m = ds.find((x) => x.khoa === khoa);
    if (!m || dangChay) return;
    setDangChay(true);
    ketThuc((await gui(m)) ? 1 : 0);
    setDangChay(false);
  };

  const guiTatCa = async () => {
    if (dangChay) return;
    setDangChay(true);
    let soXong = 0;
    for (const m of ds.filter((x) => x.trangThai === 'cho' || x.trangThai === 'loi')) {
      if (await gui(m)) soXong += 1;
    }
    ketThuc(soXong);
    setDangChay(false);
  };

  return {
    ds,
    dangChay,
    them,
    sua,
    bo: (khoa: string) => setDs((cu) => cu.filter((m) => m.khoa !== khoa)),
    boDaXong: () => setDs((cu) => cu.filter((m) => m.trangThai !== 'xong')),
    /** Đặt một bộ cho mọi mục còn chờ hay lỗi. */
    datBoChoTatCa: (kbId: string) =>
      setDs((cu) => cu.map((m) => (m.trangThai === 'cho' || m.trangThai === 'loi' ? { ...m, kbId } : m))),
    guiMot,
    guiTatCa,
  };
}
